import React, { useState, useEffect, useRef, useMemo } from 'react';
import Table from '../../utils/reuseTable';
import axios from 'axios';
import Autocomplete from '../../utils/autocomplete';
import AddShopifyMerchant from './addShopifyMerchant';
import { BooleanInput, DropDownInput, TextInput } from '../../utils/reuseAbleInputs';

import { useDispatch, useSelector } from 'react-redux';
import { fetchMerchantList } from '../../../store/actions/merchantList';
import { checkPrevAndCurrData } from '../../utils/comparingData';

const Merchants = () => {
  const [data, setData] = useState([]);
  const [searchFlag, setSearchFlag] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState();
  const [totalPages, setTotalPages] = useState();

  // Memoize merchant list to prevent unnecessary re-renders
  const merchantList = useSelector((state) => state.merchantList);
  const dispatch = useDispatch();

  // Memoize merchant names for autocomplete
  const merchantNames = useMemo(() => {
    return merchantList.map((u) => u.name);
  }, [merchantList]);

  const [search, setSearch] = useState({
    merchantID: '',
    merchantName: '',
    website: '',
    domain: '',
    platform: '',
    merchant_status: '',
    kyc_completion_status: '',
    rto_enabled: false,
    app_onboarding_status: '',
  });

  const prevData = useRef({
    searchFlag: searchFlag,
    pageSize: pageSize,
  });

  const searchGO = () => {
    setPage(1);
    let MID;
    for (let i = 0; i < merchantList.length; i++) {
      if (search.merchantName == merchantList[i].name && search.merchantName) {
        MID = merchantList[i].m_id;
        break;
      } else if (search.merchantID == merchantList[i].id) {
        MID = merchantList[i].m_id;
        break;
      }
    }
    // Make API call directly for explicit user actions
    makeApiCall(MID, true);
  };

  const colomnsName = [
    'S.No',
    'Merchant ID',
    'Merchant Name',
    'Status',
    'Products',
    'Platform Tags',
    'Device',
    'Website',
    'Active Status',
    'Go Live Date',
    'RTO Sol Live',
    'COD Blocking',
    'A/B',
    'Models in Shadow',
    'RTO COD Fee',
    'UPI Percentage',
    'Product ID blocking',
    'Pincode blocking',
    'UTM blocking',
    'COD Confirmation Prompt',
  ];

  const colomns = [
    'm_id',
    'business_name',
    'merchant_status',
    'product_type',
    'platform_tags',
    'merchant_device',
    'website',
    'is_active',
    'rto_go_live_date',
    'type_of_rto',
    'threshhold_rto_flag',
    'ab_control_enable',
    'is_shadow_deployed',
    'cod_charge',
    'upi_percent',
    'prod_id_block',
    'pincode_block',
    'utm_block',
    'cod_confirmation',
  ];

  // Cache for API responses
  const [apiCache, setApiCache] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isMerchantListLoading, setIsMerchantListLoading] = useState(false);
  const [merchantListLoaded, setMerchantListLoaded] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [lastApiCallParams, setLastApiCallParams] = useState(null);

  // Optimized debounce function with immediate option
  const debounce = (func, delay, immediate = false) => {
    let timer;
    return function (...args) {
      const context = this;
      const callNow = immediate && !timer;

      clearTimeout(timer);

      timer = setTimeout(() => {
        timer = null;
        if (!immediate) func.apply(context, args);
      }, delay);

      if (callNow) func.apply(context, args);
    };
  };

  function makeApiCall(mid, reset) {
    // Set loading state but don't block the entire page
    setIsLoading(true);

    let token = localStorage.getItem('token');
    let params = {
      page: reset ? 1 : page,
      pageSize: pageSize,
      sortKey: 'created_at',
      sortOrder: -1,
      searchMerchantId: mid || search.merchantID,
      searchWebsite: search.website,
      domain: search.domain,
      platform: search.platform,
      merchant_status: search.merchant_status,
      kyc_completion_status: search.kyc_completion_status !== 'All' ? search.kyc_completion_status : '',
      rto_enabled: search.rto_enabled,
      app_onboarding_status: search.app_onboarding_status,
    };

    // Create a cache key from the params
    const cacheKey = JSON.stringify(params);
    setLastApiCallParams(params);

    // Check if we have a cached response
    if (apiCache[cacheKey]) {
      // Use cached data immediately
      setData(apiCache[cacheKey].merchants);
      setTotalCount(apiCache[cacheKey].totalCount);
      setTotalPages(Math.ceil(apiCache[cacheKey].totalCount / pageSize));
      setIsLoading(false);
      if (reset) setPage(1);
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
      authorization: token,
    };

    // Set a timeout to clear the loading state if the request takes too long
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 10000); // 10 seconds timeout

    // Use setTimeout to defer the API call to the next tick
    // This helps keep the UI responsive during API calls
    setTimeout(() => {
      axios
        .get(process.env.BASE_URL + '/api/merchant/all', {
          headers: headers,
          params: params,
        })
        .then((res) => {
          clearTimeout(loadingTimeout);

          if (res.data && res.data.data) {
            const responseData = res.data.data;
            setData(responseData.merchants || []);
            let temp = Math.ceil(responseData.totalCount / pageSize);
            setTotalCount(responseData.totalCount || 0);
            setTotalPages(temp || 1);

            // Cache the response
            setApiCache((prevCache) => ({
              ...prevCache,
              [cacheKey]: {
                merchants: responseData.merchants || [],
                totalCount: responseData.totalCount || 0,
              },
            }));
          }

          setIsLoading(false);
        })
        .catch((err) => {
          clearTimeout(loadingTimeout);
          console.error('Error fetching merchant data:', err);
          setIsLoading(false);
        });
    }, 0);

    if (reset) setPage(1);
  }

  // Debounced version of makeApiCall with increased delay
  const debouncedMakeApiCall = useRef(debounce((mid, reset) => makeApiCall(mid, reset), 500, false)).current;

  // Only fetch merchant list when search field is focused and not already loaded
  useEffect(() => {
    // Only fetch if search is focused and list hasn't been loaded yet
    if (isSearchFocused && !merchantListLoaded && !merchantList.length) {
      setIsMerchantListLoading(true);

      dispatch(fetchMerchantList())
        .then(() => {
          setIsMerchantListLoading(false);
          setMerchantListLoaded(true);
        })
        .catch(() => {
          setIsMerchantListLoading(false);
        });
    }
  }, [isSearchFocused, merchantListLoaded, merchantList.length]);

  // Load initial data on component mount
  useEffect(() => {
    // Load initial data regardless of merchant list status
    makeApiCall(null, true);
  }, []);

  const currdata = {
    searchFlag,
    pageSize,
  };

  // Data loading effect - independent of merchant list loading
  useEffect(() => {
    if (checkPrevAndCurrData(currdata, prevData.current)) {
      debouncedMakeApiCall(null, true);
      for (const key in currdata) {
        prevData.current[key] = currdata[key];
      }
    } else {
      debouncedMakeApiCall();
    }
  }, [searchFlag, page, pageSize, search.rto_enabled]);

  return (
    <>
      <div className='d-flex justify-content-between'>
        <span class='fs30 lh-35 metro-m text-d-blue margin-b10'>Merchants</span>
      </div>
      <div className='row bg-white padding-y15 bora-6 margin-0'>
        <div className='col-sm-2'>
          <div className='form-group metro-r'>
            <label htmlFor='pid' className='fs14 lh-16 metro-r text-l-blue'>
              Merchant Name
            </label>
            {/* Only block the autocomplete component during merchant list loading */}
            <div className={isMerchantListLoading ? 'position-relative' : ''}>
              {isMerchantListLoading && (
                <div className='form-control'>
                  <span>Loading merchants...</span>
                </div>
              )}

              {!isMerchantListLoading && (
                <div className='position-relative'>
                  {!merchantListLoaded && !isSearchFocused && (
                    <div
                      className='form-control cursor-pointer'
                      onClick={() => setIsSearchFocused(true)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span>Search merchants...</span>
                    </div>
                  )}

                  {(merchantListLoaded || isSearchFocused) &&
                    (merchantList && merchantList.length > 0 ? (
                      <Autocomplete
                        fullMerchantList={merchantList}
                        list={merchantNames}
                        value={search.merchantName}
                        onChange={(e) => {
                          const currentInput = e.target.value;
                          setSearch({ ...search, merchantName: currentInput });

                          // Only make API call when a merchant is fully selected
                          const index = merchantNames.indexOf(currentInput);
                          if (index !== -1) {
                            // Use the debounced version of makeApiCall
                            debouncedMakeApiCall(merchantList[index].m_id, true);
                          } else if (currentInput === '') {
                            // Only reset search when input is cleared
                            debouncedMakeApiCall(null, true);
                          }
                          // Don't make API calls for partial typing
                        }}
                        onFocus={() => setIsSearchFocused(true)}
                        onBlur={() => setIsSearchFocused(false)}
                        placeholder='Search Merchant'
                      />
                    ) : (
                      <div className='form-control'>
                        <span>No merchants found</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <TextInput colWidth={2} label='Merchant ID' data={search} dataKey='merchantID' setData={setSearch} />
        <TextInput colWidth={2} label='Website' data={search} dataKey='website' setData={setSearch} />
        <TextInput colWidth={2} label='Domain Name' data={search} dataKey='domain' setData={setSearch} />
        <DropDownInput
          colWidth={2}
          label='Merchant Platform'
          data={search}
          dataKey='platform'
          setData={setSearch}
          options={[
            'Android',
            'iOS',
            'React Native',
            'Flutter',
            'Shopify',
            'WooCommerce',
            'Magento',
            'Custom',
            'Shopify App',
          ]}
        />
        {/* <DropDownInput
          colWidth={2}
          label='Merchant Status'
          data={search}
          dataKey='merchant_status'
          setData={setSearch}
          options={['Live', 'On Hold', 'Test', 'UnLive', 'Under Integration', 'Dummy', 'Churn']}
        />
        <BooleanInput colWidth={2} label='Enable RTO' data={search} dataKey='rto_enabled' setData={setSearch} />
        <div className='form-group metro-r margin-r10 margin-l10'>
          <label htmlFor='kyc-status-filter' className='fs14 lh-16 metro-r text-l-blue'>
            KYC Status
          </label>
          <select
            onChange={(e) => {
              setSearch((prev) => ({
                ...prev,
                kyc_completion_status: e.target.value,
              }));
            }}
            className='form-control arrow-btn'
            id='kyc-status-filter'
            value={search.kyc_completion_status}
          >
            <option className='placeholder' value='' selected>
              Please select
            </option>
            <option value='All'>All</option>
            <option value='ContactInfo'>Contact Info</option>
            <option value='BusinessInformation'>Business Information</option>
            <option value='BusinessIdentity'>Business Identity</option>
            <option value='BankAccountDetails'>Bank Account Details</option>
            <option value='DocumentsUpload'>Documents Upload</option>
            <option value='ApprovalPending'>Approval Pending</option>
            <option value='Approved'>Approved</option>
            <option value='Pending'>Pending</option>
          </select>
        </div>
        <DropDownInput
          colWidth={3}
          label='App Onboarding Status'
          placeholder='Please Select'
          data={search}
          dataKey='app_onboarding_status'
          setData={setSearch}
          options={[
            'Feature tour complete',
            'Pricing approval pending',
            'Pricing approval complete',
            'KYC basic info added',
            'KYC address details added',
            'KYC bank details added',
            'KYC business details added',
            'KYC document upload complete',
            'KYC verification pending',
            'KYC verification failed',
            'KYC verification complete',
            'COD App installed',
            'COD App activated',
            'Payment App installed',
            'Payment App activated',
            'Kwik checkout button activated',
            'One time login complete',
            'Onboarding complete',
          ]}
          optionValues={[
            'feature_tour_done',
            'pricing_approval_pending',
            'pricing_details_confirmed',
            'kyc_basic_info_added',
            'kyc_address_details',
            'kyc_bank_details',
            'kyc_business_details',
            'kyc_document_upload',
            'kyc_verification_pending',
            'kyc_verification_failed',
            'kyc_completed',
            'smart_cod_app_installed',
            'smart_cod_app_activated',
            'kwik_pay_app_installed',
            'kwik_pay_app_activated',
            'kwik_checkout_button_activated',
            'one_time_login_activated',
            'setup_completed',
          ]}
        />
          options={["Feature tour complete", "Pricing approval pending", "Pricing approval complete", "KYC basic info added", "KYC address details added", "KYC bank details added", "KYC business details added", "KYC document upload complete", "KYC verification pending", "KYC verification failed", "KYC verification complete", "COD App installed", "COD App activated", "Payment App installed", "Payment App activated", "Kwik checkout button activated", "One time login complete", "Onboarding complete"]}
          optionValues={["feature_tour_done", "pricing_approval_pending", "pricing_details_confirmed", "kyc_basic_info_added", "kyc_address_details", "kyc_bank_details", "kyc_business_details", "kyc_document_upload", "kyc_verification_pending", "kyc_verification_failed", "kyc_completed", "smart_cod_app_installed", "smart_cod_app_activated", "kwik_pay_app_installed", "kwik_pay_app_activated", "kwik_checkout_button_activated", "one_time_login_activated", "setup_completed"]}
        /> */}
        <div className='col-sm-2 search-filter'>
          <div className='form-group d-flex padding-t22 metro-r align-items-center'>
            <button
              onClick={searchGO}
              className='margin-t5 blue-btn h-auto  bora-6 border-0 text-white poppins fw700 fs14 lh-21 col-6 padding-y10'
            >
              GO
            </button>
            <button
              onClick={() => {
                setPage(1);
                setSearchFlag(!searchFlag);
                setSearch({
                  merchantID: '',
                  merchantName: '',
                  website: '',
                  domain: '',
                  platform: '',
                  merchant_status: '',
                  kyc_completion_status: '',
                  rto_enabled: false,
                  app_onboarding_status: '',
                });
                // Clear the search and refresh the data immediately
                makeApiCall(null, true);
              }}
              className=' margin-t5 reset-btn fs16 lh-16 metro-r text-t-blue btn margin-l10 '
            >
              Reset
            </button>
          </div>
        </div>
      </div>
      <div className='bg-white margin-t10 padding-15'>
        {totalCount == 0 && !isLoading ? (
          <h3 className='text-center'>No Data Found</h3>
        ) : (
          <div className='position-relative'>
            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  padding: '5px 10px',
                  background: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '3px',
                  zIndex: '1',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  animation: 'fadeIn 0.3s ease-in-out',
                }}
              >
                <span className='fs12 text-l-blue'>
                  <span style={{ display: 'inline-block', marginRight: '5px' }}>Loading data</span>
                  <span
                    style={{
                      display: 'inline-block',
                      animation: 'ellipsis 1.5s infinite',
                    }}
                  >
                    ...
                  </span>
                </span>
              </div>
            )}
            <style jsx>{`
              @keyframes fadeIn {
                from {
                  opacity: 0;
                }
                to {
                  opacity: 1;
                }
              }
              @keyframes ellipsis {
                0% {
                  opacity: 0.3;
                }
                50% {
                  opacity: 1;
                }
                100% {
                  opacity: 0.3;
                }
              }
            `}</style>
            <Table
              colomnsName={!search.rto_enabled ? colomnsName.slice(0, 9) : colomnsName}
              colomns={!search.rto_enabled ? colomns.slice(0, 9) : colomns}
              data={data}
              totalCount={totalCount}
              setPageSize={setPageSize}
              pageSize={pageSize}
              page={page}
              setSearchFlag={setSearchFlag}
              setPage={setPage}
              pages={totalPages}
              from={'merchants'}
            />
          </div>
        )}
      </div>
      <AddShopifyMerchant searchFlag={searchFlag} setSearchFlag={setSearchFlag} />
    </>
  );
};

export default Merchants;
